
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  api,
  athletesAPI,
  goalsAPI,
  notificationsAPI,
  stravaAPI,
  trainingPreferencesAPI,
} from '../api';
import { useTranslation } from '../i18n';
import { toast } from '../components/common/Notifier';

import { DatePicker } from '../components/preferences/DatePicker';

import { TELEGRAM_BOT_USERNAME } from '../constants/bot';
import { COUNTRIES, MIN_DAYS_BETWEEN_RACES, WEEKDAY_KEYS } from '../constants/preferences';
import { DISTANCE_PRESETS } from '../constants/races';
import { formatLastSynced } from '../utils/formatLastSynced';
import { translateErrorKey } from '../utils/errorMessage';

// Хелпер для безопасного парсинга даты в Date объект
function parseToDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

export const PreferencesPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { athlete, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const hasCheckedControlGoal = useRef(false);

  // --- Вес и результат ---
  const [weight, setWeight] = useState('');
  const [savingWeight, setSavingWeight] = useState(false);
  const [resultDistance, setResultDistance] = useState('');
  const [resultMin, setResultMin] = useState('');
  const [resultSec, setResultSec] = useState('');
  const [savingResult, setSavingResult] = useState(false);

  // --- Город / Страна ---
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [cityError, setCityError] = useState('');
  const [countryInput, setCountryInput] = useState('');
  const [showCountryInput, setShowCountryInput] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);

  // --- Настройки недели ---
  const [intervalsPerWeek, setIntervalsPerWeek] = useState(2);
  const [longRunWeekday, setLongRunWeekday] = useState(6);
  const [mpSegmentMode, setMpSegmentMode] = useState('always');
  const [restWeekdays, setRestWeekdays] = useState([]);
  const [savingWeek, setSavingWeek] = useState(false);

  const [weeklyVolumeInput, setWeeklyVolumeInput] = useState('');
  const [savingWeeklyVolume, setSavingWeeklyVolume] = useState(false);

  // --- WARMUP / COOLDOWN ---
  const [warmupKm, setWarmupKm] = useState(3.0);
  const [cooldownKm, setCooldownKm] = useState(2.0);
  const [longRunWarmupKm, setLongRunWarmupKm] = useState(0.0);
  const [longRunCooldownKm, setLongRunCooldownKm] = useState(0.0);
  const [savingWarmupCooldown, setSavingWarmupCooldown] = useState(false);

  // --- Календарь стартов ---
  const [goals, setGoals] = useState({
    A: { distance_m: '', date: null },
    B: { distance_m: '', date: null },
    C: { distance_m: '', date: null },
  });
  const [savingGoals, setSavingGoals] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // --- Telegram & Strava ---
  const telegramLinked = Boolean(athlete?.telegram_id);
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaInfo, setStravaInfo] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // --- Уведомления ---
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [quietStart, setQuietStart] = useState('22:00');
  const [quietEnd, setQuietEnd] = useState('07:00');
  const [reminderInterval, setReminderInterval] = useState(2);
  const [savingNotif, setSavingNotif] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);

  const [resultMessage, setResultMessage] = useState(null);
  const [hrMaxData, setHrMaxData] = useState({ observed_max_hr: '' });
  const [hrEstimateData, setHrEstimateData] = useState({ race_distance_m: '5000', hr_avg_last_1km: '' });

  const priorityLabels = useMemo(() => ({
    A: t('preferences.goal_a'),
    B: t('preferences.goal_b'),
    C: t('preferences.goal_c'),
  }), [t]);

  // Загрузка настроек тренировок
  const loadPreferences = useCallback(async () => {
    if (!athlete?.id) return;
    try {
      const response = await trainingPreferencesAPI.get(athlete.id);
      const data = response.data;
      setIntervalsPerWeek(data.intervals_per_week ?? 2);
      setLongRunWeekday(data.long_run_weekday ?? 6);
      setRestWeekdays(data.rest_weekdays ?? []);
      setWarmupKm(data.warmup_km ?? 3.0);
      setCooldownKm(data.cooldown_km ?? 2.0);
      setLongRunWarmupKm(data.long_run_warmup_km ?? 0.0);
      setLongRunCooldownKm(data.long_run_cooldown_km ?? 0.0);
      setMpSegmentMode(data.mp_segment_mode ?? 'always');
      setCity(data.city ?? '');
      setCountry(data.country ?? '');
    } catch {
      // Использовать значения по умолчанию
    }
  }, [athlete?.id]);

  // Загрузка целей
  const loadGoals = useCallback(async () => {
    if (!athlete?.id) return;
    try {
      const response = await goalsAPI.list({ athlete_id: athlete.id, status: 'planned' });
      const goalData = {
        A: { distance_m: '', date: null },
        B: { distance_m: '', date: null },
        C: { distance_m: '', date: null },
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      response.data
        .filter((g) => g.status === 'planned' && parseToDate(g.date) >= today)
        .forEach((g) => {
          if (g.priority in goalData) {
            goalData[g.priority] = {
              distance_m: g.distance_m || '',
              date: g.date || null,
            };
          }
        });

      setGoals(goalData);
    } catch {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  }, [athlete?.id, t]);

  useEffect(() => {
    if (athlete?.id) {
      loadPreferences();
      loadGoals();
    }
  }, [athlete?.id, loadPreferences, loadGoals]);

  // Проверка статуса Strava
  useEffect(() => {
    stravaAPI
      .status()
      .then(({ data }) => {
        setStravaConnected(data.connected);
        if (data.connected) {
          setStravaInfo({
            firstname: data.firstname,
            profile_photo: data.profile_photo,
            last_synced_at: data.last_synced_at,
          });
        }
      })
      .catch(() => setStravaConnected(false));
  }, []);

  // Автоматическое добавление контрольного старта (C) если нет стартов в ближайшие 8 недель
  useEffect(() => {
    if (loading || hasCheckedControlGoal.current) return;

    const hasC = goals.C?.distance_m && goals.C?.date;
    if (hasC) {
      hasCheckedControlGoal.current = true;
      return;
    }

    const now = new Date();
    const eightWeeksLater = new Date(now.getTime() + 8 * 7 * 24 * 60 * 60 * 1000);

    const hasUpcoming = ['A', 'B', 'C'].some((priority) => {
      const d = parseToDate(goals[priority]?.date);
      if (!d) return false;
      return d >= now && d <= eightWeeksLater;
    });

    if (!hasUpcoming) {
      const fourWeeksLater = new Date();
      fourWeeksLater.setDate(fourWeeksLater.getDate() + 28);
      const isoDate = fourWeeksLater.toISOString().slice(0, 10);

      setGoals((prev) => ({
        ...prev,
        C: { distance_m: 5000, date: isoDate },
      }));
      hasCheckedControlGoal.current = true;
    }
  }, [goals, loading]);

  const hasUpcomingGoals = useCallback(() => {
    const now = new Date();
    const eightWeeksLater = new Date(now.getTime() + 8 * 7 * 24 * 60 * 60 * 1000);

    return ['A', 'B', 'C'].some((priority) => {
      const d = parseToDate(goals[priority]?.date);
      if (!d) return false;
      return d >= now && d <= eightWeeksLater;
    });
  }, [goals]);

  // Скролл к секции по хешу в URL
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightedId(id);
        setTimeout(() => setHighlightedId(null), 2000);
      }
    }
  }, [location.hash]);

  const updateGoal = (priority, field, value) => {
    setGoals((prev) => ({
      ...prev,
      [priority]: { ...prev[priority], [field]: value },
    }));
  };

  // Сохранение локации
  const handleSaveLocation = async (e) => {
    e.preventDefault();
    setSavingLocation(true);
    setCityError('');

    if (city && !country) {
      setCityError(t('preferences.country_required'));
      toast.error(t('preferences.country_required'));
      setSavingLocation(false);
      return;
    }

    try {
      await api.post('/training-preferences/', {
        athlete_id: athlete.id,
        city,
        country,
        intervals_per_week: intervalsPerWeek,
        long_run_weekday: longRunWeekday,
        rest_weekdays: restWeekdays,
        warmup_km: warmupKm,
        cooldown_km: cooldownKm,
        long_run_warmup_km: longRunWarmupKm,
        long_run_cooldown_km: longRunCooldownKm,
        mp_segment_mode: mpSegmentMode,
      });

      toast.success(t('preferences.location_saved'));
      await loadPreferences();
    } catch (error) {
      const data = error.response?.data;
      if (data?.error === 'city_not_found') {
        const msg = t('preferences.city_not_found_with_country', {
          city: data?.city || city,
          country: data?.country || country,
        });
        setCityError(msg);
        toast.error(msg);
      } else {
        toast.error(data?.message || data?.error || t('error.general'));
      }
    } finally {
      setSavingLocation(false);
    }
  };

  // Сохранение структуры недели
  const handleSaveWeekSettings = async (e) => {
    e.preventDefault();
    setSavingWeek(true);

    try {
      const { data } = await api.post('/training-preferences/', {
        athlete_id: athlete.id,
        intervals_per_week: intervalsPerWeek,
        long_run_weekday: longRunWeekday,
        rest_weekdays: restWeekdays,
        warmup_km: warmupKm,
        cooldown_km: cooldownKm,
        long_run_warmup_km: longRunWarmupKm,
        long_run_cooldown_km: longRunCooldownKm,
        mp_segment_mode: mpSegmentMode,
        city,
        country,
      });

      if (data.schedule_changed) {
        toast.info(t('preferences.schedule_reset_notice'));
      } else {
        toast.success(t('common.success'));
      }
      await loadPreferences();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || t('error.general'));
    } finally {
      setSavingWeek(false);
    }
  };

  // Сохранение разминки и заминки
  const handleSaveWarmupCooldown = async () => {
    setSavingWarmupCooldown(true);
    try {
      await api.post('/training-preferences/', {
        athlete_id: athlete.id,
        intervals_per_week: intervalsPerWeek,
        long_run_weekday: longRunWeekday,
        rest_weekdays: restWeekdays,
        warmup_km: warmupKm,
        cooldown_km: cooldownKm,
        long_run_warmup_km: longRunWarmupKm,
        long_run_cooldown_km: longRunCooldownKm,
        mp_segment_mode: mpSegmentMode,
        city,
        country,
      });
      toast.success(t('preferences.warmup_cooldown_saved'));
      await loadPreferences();
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingWarmupCooldown(false);
    }
  };

  // Сохранение целей (Старты A/B/C)
  const handleSaveGoals = async (e) => {
    e.preventDefault();
    setSavingGoals(true);

    try {
      const hasAnyGoal = ['A', 'B', 'C'].some(
        (p) => goals[p]?.distance_m && goals[p]?.date
      );

      if (!hasAnyGoal) {
        toast.error(t('preferences.no_goals_filled'));
        setSavingGoals(false);
        return;
      }

      const filledGoals = ['A', 'B', 'C']
        .filter((p) => goals[p]?.distance_m && goals[p]?.date)
        .map((p) => ({
          priority: p,
          dateStr: typeof goals[p].date === 'string' ? goals[p].date : goals[p].date.toISOString().slice(0, 10),
          dateObj: parseToDate(goals[p].date),
        }));

      // Проверка: дата не в прошлом
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      for (const g of filledGoals) {
        if (!g.dateObj || g.dateObj < today) {
          toast.error(`${t('preferences.date_in_past')} (${g.priority})`);
          setSavingGoals(false);
          return;
        }
      }

      // Проверка: минимум 6 дней между стартами
      for (let i = 0; i < filledGoals.length; i++) {
        for (let j = i + 1; j < filledGoals.length; j++) {
          const diffDays = Math.abs(
            (filledGoals[i].dateObj.getTime() - filledGoals[j].dateObj.getTime()) / (1000 * 60 * 60 * 24)
          );
          if (diffDays < MIN_DAYS_BETWEEN_RACES) {
            toast.error(
              `${t('preferences.races_too_close')} (${filledGoals[i].priority} / ${filledGoals[j].priority})`
            );
            setSavingGoals(false);
            return;
          }
        }
      }

      // Удаляем старые и сохраняем новые цели
      await goalsAPI.deleteAll({ athlete_id: athlete.id });

      for (const [priority, data] of Object.entries(goals)) {
        if (data.distance_m && data.date) {
          const isoDate = typeof data.date === 'string' ? data.date : data.date.toISOString().slice(0, 10);
          await goalsAPI.create({
            athlete_id: athlete.id,
            distance_m: parseFloat(data.distance_m),
            date: isoDate,
            priority,
            status: 'planned',
          });
        }
      }

      toast.success(t('preferences.goals_saved'));
      await loadGoals();
    } catch (error) {
      console.error('Save goals error:', error);
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingGoals(false);
    }
  };

  // Сохранение контрольного результата (VDOT)
  const handleSaveResult = async (e) => {
    e.preventDefault();
    if (!resultDistance || resultMin === '' || resultSec === '') return;

    setSavingResult(true);
    try {
      const { data } = await api.post('/race-results/', {
        athlete: athlete.id,
        distance_m: parseFloat(resultDistance),
        time_min: parseInt(resultMin, 10),
        time_sec: parseInt(resultSec, 10),
        date: new Date().toISOString().slice(0, 10),
        source: 'assessment',
      });

      setResultMessage({ vdot: data.calculated_vdot, commentKey: data.vdot_comment_key });
      toast.success(t('preferences.result_saved'));
      setResultDistance('');
      setResultMin('');
      setResultSec('');
      await checkAuth();
    } catch (error) {
      const errKey = error.response?.data?.error;
      toast.error(translateErrorKey(errKey, t));
    } finally {
      setSavingResult(false);
    }
  };

  const handleAddHrMax = async (e) => {
    e.preventDefault();
    if (!hrMaxData.observed_max_hr) {
      toast.error(t('profile.hr_max_required'));
      return;
    }
    setLoading(true);
    try {
      await athletesAPI.addHrMaxObservation(athlete.id, parseInt(hrMaxData.observed_max_hr, 10));
      await checkAuth();
      toast.success(t('profile.hr_max_added'));
      setHrMaxData({ observed_max_hr: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    }
    setLoading(false);
  };

  const handleAddHrEstimate = async (e) => {
    e.preventDefault();
    if (!hrEstimateData.hr_avg_last_1km) {
      toast.error(t('profile.hr_estimate_required'));
      return;
    }
    setLoading(true);
    try {
      await athletesAPI.addHrMaxEstimate(
        athlete.id,
        parseInt(hrEstimateData.race_distance_m, 10),
        parseInt(hrEstimateData.hr_avg_last_1km, 10)
      );
      await checkAuth();
      toast.success(t('profile.hr_estimate_added'));
      setHrEstimateData({ race_distance_m: '5000', hr_avg_last_1km: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    }
    setLoading(false);
  };

  const handleSaveWeeklyVolume = async (e) => {
    e.preventDefault();
    if (!weeklyVolumeInput || isNaN(parseFloat(weeklyVolumeInput))) return;

    setSavingWeeklyVolume(true);
    try {
      await athletesAPI.updateMe({ weekly_volume_km: parseFloat(weeklyVolumeInput) });
      setWeeklyVolumeInput('');
      toast.success(t('common.success'));
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.message || t('error.general'));
    } finally {
      setSavingWeeklyVolume(false);
    }
  };

  const handleSaveWeight = async (e) => {
    e.preventDefault();
    if (!weight || isNaN(parseFloat(weight))) return;

    setSavingWeight(true);
    try {
      await athletesAPI.addWeight(athlete.id, parseFloat(weight));
      setWeight('');
      toast.success(t('common.weight_saved'));
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingWeight(false);
    }
  };

  const toggleRestDay = (day) => {
    setRestWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleConnectTelegram = async () => {
    try {
      const { data } = await athletesAPI.getTelegramLinkToken();
      window.open(`https://t.me/${TELEGRAM_BOT_USERNAME}?start=${data.token}`, '_blank');

      let attempts = 0;
      const interval = setInterval(async () => {
        attempts += 1;
        const updated = await checkAuth();
        if (updated?.telegram_id || attempts >= 30) {
          clearInterval(interval);
        }
      }, 2000);
    } catch {
      toast.error(t('preferences.telegram_link_error'));
    }
  };

  const handleDisconnectTelegram = async () => {
    try {
      await athletesAPI.disconnectTelegram();
      toast.success(t('preferences.telegram_disconnected'));
      await checkAuth();
    } catch {
      toast.error(t('preferences.telegram_disconnect_error'));
    }
  };

  const handleDisconnectStrava = async () => {
    try {
      await stravaAPI.disconnect();
      setStravaConnected(false);
      setStravaInfo(null);
      toast.success(t('preferences.strava_disconnected'));
    } catch {
      toast.error(t('error.general'));
    }
  };

  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setSavingNotif(true);
    try {
      await notificationsAPI.save({
        athlete_id: athlete.id,
        enabled: notifEnabled,
        quiet_hours_start: quietStart,
        quiet_hours_end: quietEnd,
        reminder_interval_hours: reminderInterval,
      });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingNotif(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">{t('preferences.title')}</h1>
        <Link to="/dashboard" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
          ← {t('preferences.back_to_dashboard')}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ЛЕВАЯ КОЛОНКА */}
        <div className="space-y-6">
          {/* Настройки недели */}
          <form
            id="week-section"
            onSubmit={handleSaveWeekSettings}
            className={`bg-white rounded-xl shadow p-6 space-y-4 transition-all ${
              highlightedId === 'week-section' ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-800">📅 {t('preferences.week_title')}</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('preferences.intervals_per_week')}
              </label>
              <select
                value={intervalsPerWeek}
                onChange={(e) => setIntervalsPerWeek(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('preferences.long_run_day')}
              </label>
              <select
                value={longRunWeekday}
                onChange={(e) => setLongRunWeekday(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {WEEKDAY_KEYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {t(`preferences.weekday_${d.key}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('preferences.mp_segment_mode_label')}
              </label>
              <select
                value={mpSegmentMode}
                onChange={(e) => setMpSegmentMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="always">{t('preferences.mp_segment_always')}</option>
                <option value="never">{t('preferences.mp_segment_never')}</option>
                <option value="random">{t('preferences.mp_segment_random')}</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('preferences.mp_segment_hint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('preferences.rest_days')}
              </label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAY_KEYS.map((d) => (
                  <button
                    type="button"
                    key={d.value}
                    onClick={() => toggleRestDay(d.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      restWeekdays.includes(d.value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t(`preferences.weekday_${d.key}`)}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={savingWeek}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              {savingWeek ? t('common.loading') : t('preferences.save')}
            </button>
          </form>

          {/* Недельный объём */}
          <form
            id="weekly-volume-section"
            onSubmit={handleSaveWeeklyVolume}
            className={`bg-white rounded-xl shadow p-6 space-y-3 transition-all ${
              highlightedId === 'weekly-volume-section' ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-800">📏 {t('preferences.weekly_volume_title')}</h2>

            {athlete?.weekly_volume_km && (
              <p className="text-sm text-gray-500">
                {t('preferences.current_weekly_volume')}:{' '}
                <span className="font-semibold text-gray-700">{athlete.weekly_volume_km} км</span>
              </p>
            )}

            <div className="flex gap-3">
              <input
                type="number"
                step="1"
                value={weeklyVolumeInput}
                onChange={(e) => setWeeklyVolumeInput(e.target.value)}
                placeholder={t('preferences.weekly_volume_placeholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={savingWeeklyVolume}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                {t('preferences.save')}
              </button>
            </div>
          </form>

          {/* Результат забега — обновление VDOT */}
          <form
            id="vdot-section"
            onSubmit={handleSaveResult}
            className={`bg-white rounded-xl shadow p-6 space-y-3 transition-all ${
              highlightedId === 'vdot-section' ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-800">🏁 {t('preferences.result_title')}</h2>

            {athlete?.current_vdot && (
              <p className="text-sm text-gray-500">
                {t('preferences.current_vdot')}: <span className="font-semibold text-gray-700">{athlete.current_vdot}</span>
              </p>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('preferences.distance')}</label>
                <input
                  type="number"
                  value={resultDistance}
                  onChange={(e) => setResultDistance(e.target.value)}
                  placeholder="5000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('preferences.minutes')}</label>
                <input
                  type="number"
                  value={resultMin}
                  onChange={(e) => setResultMin(e.target.value)}
                  placeholder="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('preferences.seconds')}</label>
                <input
                  type="number"
                  value={resultSec}
                  onChange={(e) => setResultSec(e.target.value)}
                  placeholder="00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={savingResult}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              {t('preferences.save')}
            </button>

            {resultMessage && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-gray-700">
                VDOT: <strong>{resultMessage.vdot}</strong>
                {resultMessage.commentKey && <div className="mt-1">{t(resultMessage.commentKey)}</div>}
              </div>
            )}
          </form>

          {/* Календарь стартов A/B/C */}
          <form onSubmit={handleSaveGoals} className="bg-white rounded-xl shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">🎯 {t('preferences.goals_title')}</h2>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 font-bold text-sm flex items-center justify-center transition-all"
                >
                  ?
                </button>

                {showTooltip && (
                  <div className="absolute right-0 top-10 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 text-left">
                    <button
                      onClick={() => setShowTooltip(false)}
                      className="float-right text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-semibold text-green-600">🟢 Старт C</span>
                        <p className="text-gray-600 text-xs mt-1">{t('preferences.goal_c_info')}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-blue-600">🔵 Старт B</span>
                        <p className="text-gray-600 text-xs mt-1">{t('preferences.goal_b_info')}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-red-600">🔴 Старт A</span>
                        <p className="text-gray-600 text-xs mt-1">{t('preferences.goal_a_info')}</p>
                      </div>
                      <div className="border-t pt-2 mt-2">
                        <p className="text-xs text-gray-500">💡 {t('preferences.goal_auto_info')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-500">{t('preferences.goals_desc')}</p>

            {['A', 'B', 'C'].map((priority) => (
              <div key={priority} className="grid grid-cols-2 gap-3 items-end border-t pt-3 first:border-t-0 first:pt-0">
                <div className="col-span-2 text-sm font-medium text-gray-700">
                  {priorityLabels[priority]}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('preferences.distance')}</label>
                  <input
                    type="number"
                    list={`distance-presets-${priority}`}
                    value={goals[priority]?.distance_m || ''}
                    onChange={(e) => updateGoal(priority, 'distance_m', e.target.value)}
                    placeholder="10000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <datalist id={`distance-presets-${priority}`}>
                    {DISTANCE_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('preferences.date')}</label>
                  <DatePicker
                    value={goals[priority]?.date || null}
                    onChange={(isoDate) => {
                      if (isoDate === null) {
                        updateGoal(priority, 'date', null);
                        updateGoal(priority, 'distance_m', '');
                      } else {
                        updateGoal(priority, 'date', isoDate);
                      }
                    }}
                    placeholder={t('preferences.date_placeholder')}
                  />
                </div>
              </div>
            ))}

            {!hasUpcomingGoals() && !goals.C?.date && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                💡 {t('preferences.goal_auto_notice')}
              </div>
            )}

            <button
              type="submit"
              disabled={savingGoals}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              {t('preferences.save')}
            </button>
          </form>
        </div>

        {/* ПРАВАЯ КОЛОНКА */}
        <div className="space-y-6">
          {/* Город / страна */}
          <form
            id="location-section"
            onSubmit={handleSaveLocation}
            className="bg-white rounded-xl shadow p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold text-gray-800">
              📍 {t('preferences.city_title')}
            </h2>

            <p className="text-sm text-gray-500">{t('preferences.city_desc')}</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('preferences.city')}
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setCityError('');
                  }}
                  placeholder={t('preferences.city_placeholder')}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    cityError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />

                <div className="relative w-32">
                  {!showCountryInput ? (
                    <select
                      value={country}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setShowCountryInput(true);
                          setCountryInput(country || '');
                        } else {
                          setCountry(e.target.value);
                          setCityError('');
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        cityError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">{t('preferences.country')}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                      <option value="__custom__">✏️ {t('preferences.other_country')}</option>
                    </select>
                  ) : (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={countryInput}
                        onChange={(e) => {
                          setCountryInput(e.target.value.toUpperCase());
                          setCountry(e.target.value.toUpperCase());
                          setCityError('');
                        }}
                        placeholder={t('preferences.country_code')}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase ${
                          cityError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        maxLength={2}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowCountryInput(false);
                          setCountryInput('');
                        }}
                        className="px-2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-1">{t('preferences.city_hint')}</p>

              {cityError && <p className="text-sm text-red-600 mt-1">⚠️ {cityError}</p>}
            </div>

            <button
              type="submit"
              disabled={savingLocation}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              {savingLocation ? t('common.loading') : `💾 ${t('preferences.save_location')}`}
            </button>
          </form>

          {/* Вес */}
          <form
            id="weight-section"
            onSubmit={handleSaveWeight}
            className={`bg-white rounded-xl shadow p-6 space-y-3 transition-all ${
              highlightedId === 'weight-section' ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-800">⚖️ {t('preferences.weight_title')}</h2>

            {athlete?.weight && (
              <p className="text-sm text-gray-500">
                {t('preferences.current_weight')}:{' '}
                <span className="font-semibold text-gray-700">
                  {t('common.kg', { weight: athlete.weight })}
                </span>
              </p>
            )}

            <div className="flex gap-3">
              <input
                type="number"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={t('preferences.weight_placeholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={savingWeight}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                {t('preferences.save')}
              </button>
            </div>
          </form>

          {/* HRmax */}
          <div id="hr-max-section" className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">❤️ {t('profile.hr_max')}</h2>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">{t('profile.effective_hr_max')}</div>
              <div className="text-3xl font-bold text-red-600">
                {athlete?.effective_max_hr || '—'}
              </div>
              {athlete?.easy_hr_cap_range && (
                <div className="text-xs text-gray-500 mt-1">
                  {t('profile.easy_hr_cap')}: {athlete.easy_hr_cap_range.low}–{athlete.easy_hr_cap_range.high}
                </div>
              )}
            </div>

            {/* Ручной ввод замера */}
            <form onSubmit={handleAddHrMax} className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.add_hr_max')}
                </label>
                <input
                  type="number"
                  value={hrMaxData.observed_max_hr}
                  onChange={(e) => setHrMaxData({ observed_max_hr: e.target.value })}
                  placeholder={t('profile.hr_max_placeholder')}
                  className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg disabled:opacity-50 whitespace-nowrap"
              >
                {t('profile.add_hr_max_btn')}
              </button>
            </form>

            {/* Оценка по гонке */}
            <form onSubmit={handleAddHrEstimate} className="flex items-end gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.race_distance')}
                </label>
                <select
                  value={hrEstimateData.race_distance_m}
                  onChange={(e) => setHrEstimateData({ ...hrEstimateData, race_distance_m: e.target.value })}
                  className="mt-1 block px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="5000">5 км</option>
                  <option value="10000">10 км</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('profile.hr_avg_last_1km')}
                </label>
                <input
                  type="number"
                  value={hrEstimateData.hr_avg_last_1km}
                  onChange={(e) => setHrEstimateData({ ...hrEstimateData, hr_avg_last_1km: e.target.value })}
                  placeholder={t('profile.hr_avg_placeholder')}
                  className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-lg disabled:opacity-50 whitespace-nowrap"
              >
                {t('profile.calculate')}
              </button>
            </form>
          </div>

          {/* Разминка и заминка */}
          <div className="bg-white rounded-xl shadow p-6 space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              🏃 {t('preferences.warmup_cooldown_title')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t('preferences.warmup_km')}
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={warmupKm}
                  onChange={(e) => setWarmupKm(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t('preferences.cooldown_km')}
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={cooldownKm}
                  onChange={(e) => setCooldownKm(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t('preferences.long_run_warmup')}
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={longRunWarmupKm}
                  onChange={(e) => setLongRunWarmupKm(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t('preferences.long_run_cooldown')}
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={longRunCooldownKm}
                  onChange={(e) => setLongRunCooldownKm(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleSaveWarmupCooldown}
              disabled={savingWarmupCooldown}
              className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            >
              {savingWarmupCooldown ? t('common.loading') : `💾 ${t('preferences.save_warmup_cooldown')}`}
            </button>
          </div>

          {/* Strava */}
          <div className="bg-white rounded-xl shadow p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">
              <img src="/strava.png" alt="Strava" className="w-4 h-4 inline mr-1" /> {t('preferences.strava_title')}
            </h2>

            {stravaConnected ? (
              <>
                <div className="flex items-center gap-3">
                  {stravaInfo?.profile_photo && (
                    <img
                      src={stravaInfo.profile_photo}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="text-sm text-green-600">
                      ✅ {t('preferences.strava_connected')}
                      {stravaInfo?.firstname ? ` — ${stravaInfo.firstname}` : ''}
                    </p>
                    {stravaInfo?.last_synced_at && (
                      <p className="text-xs text-gray-400">
                        {t('preferences.strava_last_synced')}:{' '}
                        {formatLastSynced(stravaInfo.last_synced_at, t)}
                      </p>
                    )}
                  </div>
                </div>

                {syncError && <p className="text-sm text-red-500">{syncError}</p>}

                <div className="flex gap-2">
                  <button
                    onClick={handleDisconnectStrava}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
                  >
                    {t('preferences.strava_disconnect_button')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">{t('preferences.strava_desc')}</p>
                <a
                  href={stravaAPI.getAuthUrl()}
                  className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
                >
                  {t('preferences.strava_connect_button')}
                </a>
              </>
            )}
          </div>

          {/* Telegram */}
          <div className="bg-white rounded-xl shadow p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">🤖 {t('preferences.telegram_title')}</h2>
            {telegramLinked ? (
              <>
                <p className="text-sm text-green-600">✅ {t('preferences.telegram_connected')}</p>
                <button
                  onClick={handleDisconnectTelegram}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
                >
                  {t('preferences.telegram_disconnect_button')}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">{t('preferences.telegram_desc')}</p>
                <button
                  onClick={handleConnectTelegram}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
                >
                  {t('preferences.telegram_connect_button')}
                </button>
              </>
            )}
          </div>

          {/* Уведомления */}
          <form onSubmit={handleSaveNotifications} className="bg-white rounded-xl shadow p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-800">🔔 {t('preferences.notifications_title')}</h2>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={notifEnabled}
                onChange={(e) => setNotifEnabled(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">{t('preferences.notifications_enabled')}</span>
            </label>

            {notifEnabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('preferences.quiet_from')}</label>
                    <input
                      type="time"
                      value={quietStart}
                      onChange={(e) => setQuietStart(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('preferences.quiet_to')}</label>
                    <input
                      type="time"
                      value={quietEnd}
                      onChange={(e) => setQuietEnd(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('preferences.reminder_interval')}
                  </label>
                  <select
                    value={reminderInterval}
                    onChange={(e) => setReminderInterval(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[1, 2, 3, 4, 6].map((h) => (
                      <option key={h} value={h}>
                        {t('preferences.every_n_hours', { n: h })}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={savingNotif}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            >
              {t('preferences.save')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};